import React, { useState } from 'react';
import { FeedbackService, FeedbackRateLimitError } from '../services/feedbackService';
import { getCleanErrorMessage } from '../utils/errorParser.js';

const FeedbackModal = ({ 
    isOpen, 
    onClose, 
    showNotification,
    userAccount = null,
    chainId = null,
    walletType = null
}) => {
    const [subject, setSubject] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const resetForm = () => {
        setSubject('');
        setEmail('');
        setMessage('');
        setSubmitSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!message.trim()) {
            showNotification('Enter your feedback', 'error');
            return;
        }

        const trimmedEmail = email.trim();
        if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            showNotification('Enter a valid email address', 'error');
            return;
        }

        setIsSubmitting(true);
        setSubmitSuccess(false);

        try {
            const browserInfo = FeedbackService.getBrowserInfo();
            
            await FeedbackService.submitFeedback({
                type: 'feedback',
                subject: subject.trim() || 'User Feedback',
                message: message.trim(),
                email: trimmedEmail || null,
                userAddress: userAccount || null,
                chainId: chainId,
                walletType: walletType,
                browserInfo: browserInfo,
                url: window.location.href,
            });

            setSubmitSuccess(true);
            showNotification('Thank you! Your feedback has been submitted.', 'success');
            
            setTimeout(() => {
                resetForm();
                onClose();
            }, 2000);
        } catch (error) {
            console.error('Error submitting feedback:', error);
            const errorMessage =
                error instanceof FeedbackRateLimitError
                    ? error.message
                    : (getCleanErrorMessage(error) || 'Could not submit feedback. Try again.');
            showNotification(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetForm();
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal active feedback-modal" onClick={(e) => {
            if (e.target.classList.contains('modal')) {
                handleClose();
            }
        }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3 className="feedback-modal-title">Submit a Feedback</h3>
                    <button 
                        className="modal-close" 
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    {submitSuccess ? (
                        <div className="feedback-success">
                            <div className="success-icon">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <h4>Thank you!</h4>
                            <p>Your feedback has been submitted successfully. We appreciate your input!</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label" htmlFor="feedback-subject">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    id="feedback-subject"
                                    className="form-input"
                                    placeholder="e.g., Bug report, Feature request, or General feedback"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    disabled={isSubmitting}
                                    maxLength={200}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="feedback-email">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="feedback-email"
                                    className="form-input"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isSubmitting}
                                    maxLength={254}
                                    autoComplete="email"
                                />
                                <small className="form-help-text">
                                    Optional — so we can follow up if needed.
                                </small>
                            </div>

                            <div className="form-group">
                                <label className="form-label" htmlFor="feedback-message">
                                    Feedback or Bug Report *
                                </label>
                                <textarea
                                    id="feedback-message"
                                    className="form-textarea"
                                    rows="6"
                                    placeholder="Share your feedback, report a bug, or suggest improvements. For bug reports, please describe what happened, what you were doing, and any error messages you saw."
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    disabled={isSubmitting}
                                    required
                                    maxLength={2000}
                                />
                                <div className="char-count">{message.length}/2000</div>
                            </div>

                            {userAccount && (
                                <p className="form-help-text feedback-wallet-note">
                                    Connected wallet ({userAccount.slice(0, 6)}…{userAccount.slice(-4)}) will be included automatically.
                                </p>
                            )}

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isSubmitting || !message.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner"></span>
                                            Submitting…
                                        </>
                                    ) : (
                                        "Submit feedback"
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FeedbackModal;
